import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Plus, Trash2, ChevronLeft, ChevronRight, List, CalendarRange, CalendarDays, MessageSquare, Phone, User as UserIcon, Clock, FileText, Check, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = {
  i_planifikuar: "I planifikuar",
  ne_progres: "Në progres",
  perfunduar: "Përfunduar",
  anuluar: "Anuluar",
};
const STATUS_COLOR = {
  i_planifikuar: "bg-[#EAE4D9] text-[#2C4C3B] border-[#C9C2B2]",
  ne_progres: "bg-[#4A8B71]/15 text-[#2C4C3B] border-[#4A8B71]/30",
  perfunduar: "bg-[#2C4C3B] text-[#FDFBF7] border-[#2C4C3B]",
  anuluar: "bg-[#9E3C3C]/15 text-[#9E3C3C] border-[#9E3C3C]/30",
};

const DAYS_SQ = ["E Hënë","E Martë","E Mërkurë","E Enjte","E Premte","E Shtunë","E Diel"];
const MONTHS_SQ = ["Janar","Shkurt","Mars","Prill","Maj","Qershor","Korrik","Gusht","Shtator","Tetor","Nëntor","Dhjetor"];

const hhmmToMin = (s) => {
  if (!s || !/^\d{2}:\d{2}$/.test(s)) return null;
  const [h, m] = s.split(":").map(Number);
  return h * 60 + m;
};

/**
 * Build the list of visible 30-minute slot start times [{h, m}]
 * from clinic settings + doctor schedules, taking the WIDEST window
 * across every working doctor for that weekday. Falls back to 08:00-18:00.
 */
const buildSlots = (settings, schedules, dayDate) => {
  const weekday = (dayDate.getDay() + 6) % 7; // Mon=0..Sun=6
  const isSat = weekday === 5;
  const isSun = weekday === 6;
  let openMin, closeMin;
  if (isSun) {
    if (!settings?.clinic_open_sunday) return [];
    openMin  = hhmmToMin(settings?.clinic_open_time) ?? 8*60;
    closeMin = hhmmToMin(settings?.clinic_close_time) ?? 18*60;
  } else if (isSat) {
    if (settings?.clinic_open_saturday === false) return [];
    openMin  = hhmmToMin(settings?.clinic_saturday_open)  ?? 9*60;
    closeMin = hhmmToMin(settings?.clinic_saturday_close) ?? 13*60;
  } else {
    openMin  = hhmmToMin(settings?.clinic_open_time)  ?? 8*60;
    closeMin = hhmmToMin(settings?.clinic_close_time) ?? 18*60;
  }
  // Widen with each doctor's schedule for that weekday
  for (const sched of (schedules || [])) {
    const dayCfg = (sched?.days || [])[weekday];
    if (!dayCfg?.is_working) continue;
    const s = hhmmToMin(dayCfg.start_time);
    const e = hhmmToMin(dayCfg.end_time);
    if (s !== null && s < openMin)  openMin  = s;
    if (e !== null && e > closeMin) closeMin = e;
  }
  const slots = [];
  for (let m = openMin; m + 30 <= closeMin; m += 30) {
    slots.push({ h: Math.floor(m / 60), m: m % 60, min: m });
  }
  return slots;
};

const slotLabel = (s) => `${String(s.h).padStart(2, "0")}:${String(s.m).padStart(2, "0")}`;
const isTopOfHour = (s) => s.m === 0;

const toLocalISO = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const startOfWeek = (d) => {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Mon=0
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - day);
  return x;
};
const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const fmt = (iso) => iso ? new Date(iso).toLocaleString("sq-AL", { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }) : "—";
const fmtTime = (d) => d.toLocaleTimeString("sq-AL", { hour: "2-digit", minute: "2-digit", hour12: false });

export default function Appointments() {
  const { settings } = useCurrency();
  const [list, setList] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [view, setView] = useState("week"); // week | day | list
  const [cursor, setCursor] = useState(new Date());
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);  // appointment object for details dialog
  const [form, setForm] = useState({
    patient_id: "", doctor_id: "", start_time: "", duration_min: 60, reason: "", status: "i_planifikuar", notes: "",
  });

  const load = async () => {
    const [a, p, u, sc] = await Promise.all([
      api.get("/appointments"),
      api.get("/patients"),
      api.get("/doctors").catch(() => ({ data: [] })),
      api.get("/doctors/schedules/all").catch(() => ({ data: [] })),
    ]);
    setList(a.data);
    setPatients(p.data);
    const docs = (u.data || []).filter((x) => x.role === "doctor");
    setDoctors(docs);
    setSchedules(sc.data || []);
    // Auto-select i vetmi doktor (klinika 1-doktor)
    if (docs.length === 1 && !form.doctor_id) {
      setForm((f) => ({ ...f, doctor_id: docs[0].id }));
    }
  };
  useEffect(() => { load(); }, []);

  const patientName = (id) => {
    const p = patients.find((x) => x.id === id);
    return p ? `${p.first_name} ${p.last_name}` : "—";
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const startDt = new Date(form.start_time);
      const endDt = new Date(startDt.getTime() + Number(form.duration_min) * 60000);
      await api.post("/appointments", {
        patient_id: form.patient_id,
        doctor_id: form.doctor_id || null,
        reason: form.reason,
        status: form.status,
        notes: form.notes,
        duration_min: Number(form.duration_min),
        start_time: startDt.toISOString(),
        end_time: endDt.toISOString(),
      });
      toast.success("Takimi u krijua");
      setOpen(false);
      setForm({ patient_id: "", doctor_id: doctors.length === 1 ? doctors[0].id : "", start_time: "", duration_min: 60, reason: "", status: "i_planifikuar", notes: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Fshini këtë takim?")) return;
    await api.delete(`/appointments/${id}`);
    toast.success("Takimi u fshi");
    load();
  };

  // Drag-and-drop: move an appointment to a new slot, preserving duration
  const handleDrop = async (appt, newDate, newHour, newMinute = 0) => {
    try {
      const oldStart = new Date(appt.start_time);
      const oldEnd = new Date(appt.end_time);
      const durMs = oldEnd - oldStart;
      const newStart = new Date(newDate);
      newStart.setHours(newHour, newMinute, 0, 0);
      const newEnd = new Date(newStart.getTime() + (durMs || 60 * 60 * 1000));
      if (newStart.getTime() === oldStart.getTime()) return;
      await api.put(`/appointments/${appt.id}`, {
        ...appt,
        start_time: newStart.toISOString(),
        end_time: newEnd.toISOString(),
      });
      toast.success("Takimi u zhvendos");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Gabim gjatë zhvendosjes");
    }
  };

  // Quick-add: click on cell pre-fills time (defaults minute to 0 if not given)
  const openAtSlot = (date, hour, minute = 0) => {
    const s = new Date(date);
    s.setHours(hour, minute, 0, 0);
    setForm({ ...form, start_time: toLocalISO(s), duration_min: 60 });
    setOpen(true);
  };

  const weekStart = useMemo(() => startOfWeek(cursor), [cursor]);
  const weekDays = useMemo(() =>
    Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(weekStart.getDate() + i); return d; }),
    [weekStart]);

  // Slot lists (30-min granularity, union of clinic settings + doctor schedules)
  const daySlots = useMemo(() => buildSlots(settings, schedules, cursor), [settings, schedules, cursor]);
  const weekSlots = useMemo(() => {
    // Take the widest window across all 7 weekdays visible → uniform rows in the week grid
    const merged = [];
    const seen = new Set();
    for (const d of weekDays) {
      for (const s of buildSlots(settings, schedules, d)) {
        const k = s.h * 60 + s.m;
        if (!seen.has(k)) { seen.add(k); merged.push(s); }
      }
    }
    merged.sort((a, b) => a.min - b.min);
    return merged;
  }, [settings, schedules, weekDays]);

  const moveBy = (n) => {
    const d = new Date(cursor);
    if (view === "week") d.setDate(d.getDate() + n * 7);
    else if (view === "day") d.setDate(d.getDate() + n);
    setCursor(d);
  };

  const cursorLabel = view === "week"
    ? `${weekDays[0].getDate()} ${MONTHS_SQ[weekDays[0].getMonth()]} – ${weekDays[6].getDate()} ${MONTHS_SQ[weekDays[6].getMonth()]} ${weekDays[6].getFullYear()}`
    : `${DAYS_SQ[(cursor.getDay() + 6) % 7]}, ${cursor.getDate()} ${MONTHS_SQ[cursor.getMonth()]} ${cursor.getFullYear()}`;

  return (
    <div className="space-y-8" data-testid="appointments-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">Kalendari</div>
          <h1 className="mt-2 text-4xl font-semibold tracking-tight sm:text-5xl">Takimet</h1>
          <p className="mt-2 text-sm text-[#5C5C5C]">Planifikoni dhe ndiqni takimet me pacientët.</p>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="btn-primary" data-testid="add-appointment-button">
              <Plus size={16} /> Takim i Ri
            </button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-lg">
            <DialogHeader><DialogTitle>Cakto Takim të Ri</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2" data-testid="appointment-form">
              <div>
                <Label className="label-eyebrow">Pacienti</Label>
                <PatientCombobox patients={patients} value={form.patient_id}
                                 onChange={(v) => setForm({ ...form, patient_id: v })} />
              </div>
              {doctors.length > 0 && (
                <div>
                  <Label className="label-eyebrow">Mjeku</Label>
                  <Select value={form.doctor_id || "__none__"} onValueChange={(v) => setForm({ ...form, doctor_id: v === "__none__" ? "" : v })}>
                    <SelectTrigger className="mt-2" data-testid="appt-doctor-select"><SelectValue placeholder="Zgjidh mjekun" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Pa caktuar —</SelectItem>
                      {doctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-eyebrow">Fillimi</Label>
                  <Input type="datetime-local" value={form.start_time} required
                         onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                         className="mt-2" data-testid="appt-start" />
                </div>
                <div>
                  <Label className="label-eyebrow">Kohëzgjatja</Label>
                  <Select value={String(form.duration_min)} onValueChange={(v) => setForm({ ...form, duration_min: Number(v) })}>
                    <SelectTrigger className="mt-2" data-testid="appt-duration"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">15 minuta</SelectItem>
                      <SelectItem value="30">30 minuta</SelectItem>
                      <SelectItem value="45">45 minuta</SelectItem>
                      <SelectItem value="60">1 orë</SelectItem>
                      <SelectItem value="90">1 orë 30 min</SelectItem>
                      <SelectItem value="120">2 orë</SelectItem>
                      <SelectItem value="150">2 orë 30 min</SelectItem>
                      <SelectItem value="180">3 orë</SelectItem>
                      <SelectItem value="210">3 orë 30 min</SelectItem>
                      <SelectItem value="240">4 orë</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="label-eyebrow">Arsyeja</Label>
                <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                       className="mt-2" placeholder="P.sh. Kontroll periodik" data-testid="appt-reason" />
              </div>
              <div>
                <Label className="label-eyebrow">Shënime</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2" />
              </div>
              <DialogFooter>
                <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>Anulo</button>
                <button type="submit" className="btn-primary" data-testid="appt-save-button">Ruaj Takimin</button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Toolbar: navigation + view switcher */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <button onClick={() => moveBy(-1)} className="btn-ghost h-9 px-2" data-testid="prev-btn"><ChevronLeft size={16} /></button>
          <button onClick={() => setCursor(new Date())} className="btn-ghost h-9 px-4 text-sm" data-testid="today-btn">Sot</button>
          <button onClick={() => moveBy(1)} className="btn-ghost h-9 px-2" data-testid="next-btn"><ChevronRight size={16} /></button>
          <div className="ml-3 text-sm font-medium text-[#1A1A1A]">{cursorLabel}</div>
        </div>
        <div className="inline-flex rounded-full border border-[#E2DFD8] bg-white p-1">
          <ViewBtn icon={CalendarRange} label="Javë" active={view === "week"} onClick={() => setView("week")} testId="view-week" />
          <ViewBtn icon={CalendarDays} label="Ditë" active={view === "day"} onClick={() => setView("day")} testId="view-day" />
          <ViewBtn icon={List} label="Listë" active={view === "list"} onClick={() => setView("list")} testId="view-list" />
        </div>
      </div>

      {view === "list" && <ListView list={list} patientName={patientName} remove={remove} onSelect={setSelected} />}
      {view === "day" && (
        <DayGrid day={cursor} appointments={list.filter((a) => sameDay(new Date(a.start_time), cursor))}
                 patientName={patientName} onSlotClick={openAtSlot} onDelete={remove} onDrop={handleDrop} onSelect={setSelected}
                 slots={daySlots} />
      )}
      {view === "week" && (
        <WeekGrid days={weekDays} appointments={list} patientName={patientName}
                  onSlotClick={openAtSlot} onDelete={remove} onDrop={handleDrop} onSelect={setSelected}
                  slots={weekSlots} />
      )}
      <AppointmentDetails
        appointment={selected}
        onClose={() => setSelected(null)}
        patients={patients}
        doctors={doctors}
        onChanged={() => { setSelected(null); load(); }}
      />
    </div>
  );
}

function PatientCombobox({ patients, value, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = patients.find((p) => p.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" role="combobox" aria-expanded={open} data-testid="appt-patient-select"
                className="mt-2 flex h-10 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
          <span className={selected ? "" : "text-muted-foreground"}>
            {selected ? `${selected.first_name} ${selected.last_name}` : "Zgjidh pacientin"}
          </span>
          <ChevronsUpDown size={16} className="shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-white" align="start">
        <Command>
          <CommandInput placeholder="Kërko pacientin..." data-testid="appt-patient-search" />
          <CommandList>
            <CommandEmpty>Nuk u gjet asnjë pacient.</CommandEmpty>
            <CommandGroup>
              {patients.map((p) => (
                <CommandItem key={p.id} value={`${p.first_name} ${p.last_name} ${p.phone || ""}`}
                             data-testid={`appt-patient-option-${p.id}`}
                             onSelect={() => { onChange(p.id); setOpen(false); }}>
                  <Check size={14} className={`mr-2 ${value === p.id ? "opacity-100" : "opacity-0"}`} />
                  <span className="truncate">{p.first_name} {p.last_name}</span>
                  {p.phone && <span className="ml-auto pl-3 text-xs text-[#5C5C5C]">{p.phone}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function ViewBtn({ icon: Icon, label, active, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
            className={`flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              active ? "bg-[#2C4C3B] text-[#FDFBF7]" : "text-[#5C5C5C] hover:text-[#2C4C3B]"
            }`}>
      <Icon size={14} /> {label}
    </button>
  );
}

function ListView({ list, patientName, remove, onSelect }) {
  const grouped = list.reduce((acc, a) => {
    const d = new Date(a.start_time).toLocaleDateString("sq-AL", { weekday: "long", day: "2-digit", month: "long" });
    (acc[d] ||= []).push(a);
    return acc;
  }, {});
  if (Object.keys(grouped).length === 0)
    return <div className="surface-card p-10 text-center text-sm text-[#5C5C5C]">Nuk ka takime të regjistruara.</div>;
  return (
    <div className="space-y-6">
      {Object.entries(grouped).map(([day, items]) => (
        <div key={day} className="surface-card">
          <div className="px-6 py-3 border-b border-[#E2DFD8] bg-[#FDFBF7]">
            <div className="label-eyebrow">{day}</div>
          </div>
          <ul className="divide-y divide-[#E2DFD8]">
            {items.map((a) => (
              <li key={a.id} className="flex items-center justify-between px-6 py-4 hover:bg-[#FDFBF7] cursor-pointer" data-testid={`appt-${a.id}`}
                  onClick={() => onSelect(a)}>
                <div className="min-w-0">
                  <div className="font-medium truncate flex items-center gap-2">
                    {patientName(a.patient_id)} · {a.reason || "Konsultë"}
                    {a.chat_log && <MessageSquare size={12} className="text-[#2C4C3B]" title="Rezervuar nga AI Chat" />}
                  </div>
                  <div className="text-sm text-[#5C5C5C]">{fmt(a.start_time)} → {fmt(a.end_time)}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`rounded-full px-3 py-1 text-xs font-medium ${STATUS_COLOR[a.status]}`}>
                    {STATUS_LABEL[a.status]}
                  </span>
                  <button onClick={(e) => { e.stopPropagation(); remove(a.id); }} className="text-[#9E3C3C] hover:bg-[#9E3C3C]/10 p-2 rounded-full">
                    <Trash2 size={16} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function ApptBlock({ a, patientName, onDelete, onSelect, firstSlotMin }) {
  const start = new Date(a.start_time);
  const end = new Date(a.end_time);
  const startMin = start.getHours() * 60 + start.getMinutes();
  const offsetMin = Math.max(0, startMin - firstSlotMin);
  const durMin = Math.max(15, (end - start) / 60000);
  const [dragging, setDragging] = useState(false);
  const onDragStart = (e) => {
    e.stopPropagation();
    setDragging(true);
    e.dataTransfer.setData("text/plain", a.id);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragEnd = () => setDragging(false);
  // Each 30-min slot = 56px (h-14). So 1 min = 56/30 px.
  const PX_PER_MIN = 56 / 30;
  return (
    <div
      onClick={(e) => { e.stopPropagation(); onSelect && onSelect(a); }}
      className={`absolute left-1 right-1 rounded-md border px-2 py-1 text-[11px] cursor-pointer z-10 hover:z-20 hover:shadow-md transition-shadow ${STATUS_COLOR[a.status]} overflow-hidden group ${dragging ? "opacity-40" : ""}`}
      style={{ top: (offsetMin * PX_PER_MIN) + "px", height: (durMin * PX_PER_MIN) + "px", minHeight: "30px" }}
      data-testid={`appt-block-${a.id}`}
      title="Kliko për detaje"
    >
      <div className="font-semibold truncate flex items-center gap-1 leading-tight">
        {a.chat_log && <MessageSquare size={9} className="shrink-0" />}
        <span className="truncate">{patientName(a.patient_id)}</span>
      </div>
      <div className="opacity-80 truncate leading-tight">{a.reason || "Konsultë"}</div>
      <div className="text-[10px] opacity-70 leading-tight">
        {fmtTime(start)} · {a.duration_min || Math.round(durMin)} min
      </div>
      {/* Drag handle (top-left dots) — only this triggers drag */}
      <div
        draggable
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onClick={(e) => e.stopPropagation()}
        className="absolute top-0 left-0 px-1 py-0.5 cursor-move opacity-40 hover:opacity-100 text-[8px] leading-none select-none"
        title="Tërhiqe për të zhvendosur"
      >
        ⋮⋮
      </div>
      {/* Delete (top-right) */}
      <button onClick={(e) => { e.stopPropagation(); onDelete(a.id); }}
              className="absolute top-0.5 right-0.5 hidden group-hover:flex p-1 rounded-full bg-white/80 text-[#9E3C3C]" data-testid={`appt-delete-${a.id}`}>
        <Trash2 size={10} />
      </button>
    </div>
  );
}

function DropCell({ children, onSlotClick, onDrop, appts, slot, dayValue, className = "" }) {
  const [over, setOver] = useState(false);
  return (
    <div
      className={`relative h-14 border-l border-[#E2DFD8] cursor-pointer transition-colors ${
        slot.m === 0 ? "border-t border-[#E2DFD8]" : "border-t border-dashed border-[#EAE4D9]"
      } ${over ? "bg-[#EAE4D9]" : "hover:bg-[#FDFBF7]"} ${className}`}
      onClick={() => onSlotClick(dayValue, slot.h, slot.m)}
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault(); setOver(false);
        const id = e.dataTransfer.getData("text/plain");
        const found = appts.find((x) => x.id === id);
        if (found) onDrop(found, dayValue, slot.h, slot.m);
      }}
    >
      {children}
    </div>
  );
}

function DayGrid({ day, appointments, patientName, onSlotClick, onDelete, onDrop, onSelect, slots }) {
  if (!slots || slots.length === 0) {
    return (
      <div className="surface-card p-10 text-center text-sm text-[#5C5C5C]" data-testid="day-grid">
        Klinika është mbyllur këtë ditë.
      </div>
    );
  }
  const firstSlotMin = slots[0].min;
  return (
    <div className="surface-card overflow-x-auto" data-testid="day-grid">
      <div className="grid grid-cols-[60px_1fr] min-w-[400px]">
        <div></div>
        <div className="border-l border-[#E2DFD8] px-3 py-3 text-center label-eyebrow bg-[#FDFBF7]">
          {DAYS_SQ[(day.getDay() + 6) % 7]} {day.getDate()}
        </div>
        {slots.map((s, idx) => (
          <React.Fragment key={`${s.h}-${s.m}`}>
            <div className={`h-14 pr-2 pt-1 text-right text-[10px] font-mono ${
              isTopOfHour(s) ? "border-t border-[#E2DFD8] text-[#5C5C5C]" : "border-t border-dashed border-[#EAE4D9] text-[#8B7F6D]"
            }`}>
              {slotLabel(s)}
            </div>
            <DropCell appts={appointments} slot={s} dayValue={day}
                      onSlotClick={onSlotClick} onDrop={onDrop}>
              {idx === 0 && appointments.map((a) => (
                <ApptBlock key={a.id} a={a} patientName={patientName} onDelete={onDelete} onSelect={onSelect} firstSlotMin={firstSlotMin} />
              ))}
            </DropCell>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function WeekGrid({ days, appointments, patientName, onSlotClick, onDelete, onDrop, onSelect, slots }) {
  if (!slots || slots.length === 0) {
    return (
      <div className="surface-card p-10 text-center text-sm text-[#5C5C5C]" data-testid="week-grid">
        Klinika është mbyllur këto ditë.
      </div>
    );
  }
  const firstSlotMin = slots[0].min;
  return (
    <div className="surface-card overflow-x-auto" data-testid="week-grid">
      <div className="grid grid-cols-[60px_repeat(7,1fr)] min-w-[900px]">
        <div></div>
        {days.map((d, i) => (
          <div key={i} className={`border-l border-[#E2DFD8] px-3 py-3 text-center bg-[#FDFBF7] ${sameDay(d, new Date()) ? "bg-[#EAE4D9]" : ""}`}>
            <div className="label-eyebrow">{DAYS_SQ[i].slice(0, 3)}</div>
            <div className="mt-1 text-lg font-semibold tracking-tight">{d.getDate()}</div>
          </div>
        ))}
        {slots.map((s, idx) => (
          <React.Fragment key={`${s.h}-${s.m}`}>
            <div className={`h-14 pr-2 pt-1 text-right text-[10px] font-mono ${
              isTopOfHour(s) ? "border-t border-[#E2DFD8] text-[#5C5C5C]" : "border-t border-dashed border-[#EAE4D9] text-[#8B7F6D]"
            }`}>
              {slotLabel(s)}
            </div>
            {days.map((d, di) => {
              const dayAppts = idx === 0
                ? appointments.filter((a) => sameDay(new Date(a.start_time), d))
                : [];
              return (
                <DropCell key={di} appts={appointments} slot={s} dayValue={d}
                          onSlotClick={onSlotClick} onDrop={onDrop}>
                  {dayAppts.map((a) => (
                    <ApptBlock key={a.id} a={a} patientName={patientName} onDelete={onDelete} onSelect={onSelect} firstSlotMin={firstSlotMin} />
                  ))}
                </DropCell>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}


function AppointmentDetails({ appointment, onClose, patients, doctors = [], onChanged }) {
  const [patient, setPatient] = useState(null);
  const [waUrl, setWaUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [clinicName, setClinicName] = useState("klinikë");
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    start_time: "", duration_min: 60, doctor_id: "none", reason: "", notes: "",
  });

  useEffect(() => {
    api.get("/settings").then(({ data }) => setClinicName(data?.clinic_name || "klinikë")).catch(() => {});
  }, []);

  // Reset edit state whenever a new appointment is opened
  useEffect(() => {
    setEditing(false);
    if (appointment) {
      const start = new Date(appointment.start_time);
      setEditForm({
        start_time: toLocalISO(start),
        duration_min: appointment.duration_min || Math.round((new Date(appointment.end_time) - start) / 60000),
        doctor_id: appointment.doctor_id || "none",
        reason: appointment.reason || "",
        notes: appointment.notes || "",
      });
    }
  }, [appointment]);

  useEffect(() => {
    setPatient(null);
    if (!appointment) return;
    const p = patients.find((x) => x.id === appointment.patient_id);
    const setupWa = (pt) => {
      if (!pt) return;
      const when = new Date(appointment.start_time).toLocaleString("sq-AL", {
        day: "2-digit", month: "long", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: false,
      });
      const txt = `Përshëndetje ${pt.first_name || ""}, ju konfirmojmë takimin tuaj në ${clinicName} për ${when}. Ju presim!`;
      api.post("/notifications/wa-link", { phone: pt.phone || "", text: txt })
        .then(({ data }) => setWaUrl(data.url))
        .catch(() => {});
    };
    if (p) {
      setPatient(p);
      setupWa(p);
    } else if (appointment.patient_id) {
      api.get(`/patients/${appointment.patient_id}`).then(({ data }) => {
        setPatient(data);
        setupWa(data);
      }).catch(() => {});
    }
  }, [appointment, patients, clinicName]);

  if (!appointment) return null;
  const start = new Date(appointment.start_time);
  const end = new Date(appointment.end_time);

  const updateStatus = async (status) => {
    setBusy(true);
    try {
      await api.put(`/appointments/${appointment.id}`, {
        patient_id: appointment.patient_id,
        doctor_id: appointment.doctor_id,
        start_time: appointment.start_time,
        end_time: appointment.end_time,
        duration_min: appointment.duration_min || 60,
        reason: appointment.reason,
        notes: appointment.notes,
        status,
      });
      toast.success("Statusi u përditësua");
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const saveEdits = async () => {
    if (!editForm.start_time) {
      toast.error("Vendos datë dhe orë të vlefshme");
      return;
    }
    setBusy(true);
    try {
      const startDt = new Date(editForm.start_time);
      const dur = parseInt(editForm.duration_min, 10) || 60;
      const endDt = new Date(startDt.getTime() + dur * 60000);
      await api.put(`/appointments/${appointment.id}`, {
        patient_id: appointment.patient_id,
        doctor_id: editForm.doctor_id === "none" ? null : editForm.doctor_id,
        start_time: startDt.toISOString(),
        end_time: endDt.toISOString(),
        duration_min: dur,
        reason: editForm.reason,
        notes: editForm.notes,
        status: appointment.status,
      });
      toast.success("Takimi u modifikua");
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const cancelEdits = () => {
    setEditing(false);
    // reset back to current appointment values
    setEditForm({
      start_time: toLocalISO(new Date(appointment.start_time)),
      duration_min: appointment.duration_min || Math.round((end - start) / 60000),
      doctor_id: appointment.doctor_id || "none",
      reason: appointment.reason || "",
      notes: appointment.notes || "",
    });
  };

  const sendTestEmail = async () => {
    if (!patient?.email) return toast.error("Pacienti nuk ka email");
    try {
      const { data } = await api.post("/notifications/test-email", {
        to: patient.email,
        subject: `Konfirmim Takimi · DentArt`,
        body: `<p>I/E nderuar <b>${patient.first_name} ${patient.last_name}</b>,</p><p>Takimi juaj është konfirmuar për <b>${start.toLocaleString("sq-AL")}</b>.</p><p>Ju presim!</p>`,
      });
      if (data.sent) toast.success("Email-i u dërgua");
      else toast.error("SMTP s'është konfiguruar: " + (data.reason || ""));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <Dialog open={!!appointment} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="appt-details-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <span>{editing ? "Modifiko Takimin" : "Detajet e Takimit"}</span>
            {appointment.chat_log && (
              <span className="rounded-full bg-[#2C4C3B]/10 px-2 py-0.5 text-[10px] font-medium text-[#2C4C3B] flex items-center gap-1">
                <MessageSquare size={10} /> Nga AI Chat
              </span>
            )}
            {!editing && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                disabled={busy}
                className="ml-auto rounded-md bg-[#2C4C3B] text-[#FDFBF7] px-3 py-1 text-xs font-medium hover:bg-[#1F3628] disabled:opacity-50"
                data-testid="appt-edit-btn"
              >
                Edito
              </button>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Patient */}
          <section className="rounded-lg border border-[#E2DFD8] bg-[#FDFBF7] p-4">
            <div className="label-eyebrow flex items-center gap-1"><UserIcon size={12} /> Pacienti</div>
            {patient ? (
              <div className="mt-2 space-y-1">
                <div className="font-medium">{patient.first_name} {patient.last_name}</div>
                {patient.phone && (
                  <div className="flex items-center gap-3 text-sm text-[#5C5C5C] flex-wrap">
                    <a href={`tel:${patient.phone}`} className="flex items-center gap-1 hover:text-[#2C4C3B]" data-testid="appt-phone-link">
                      <Phone size={12} /> {patient.phone}
                    </a>
                    {waUrl && (
                      <a href={waUrl} target="_blank" rel="noreferrer"
                         className="flex items-center gap-1 rounded-full bg-[#25D366] text-white px-2.5 py-1 text-[11px] hover:opacity-90" data-testid="appt-wa-link">
                        <MessageSquare size={10} /> WhatsApp
                      </a>
                    )}
                  </div>
                )}
                {patient.email && (
                  <div className="text-sm text-[#5C5C5C] flex items-center gap-2">
                    {patient.email}
                    <button type="button" onClick={sendTestEmail} className="text-[11px] rounded-full bg-[#EAE4D9] px-2 py-0.5 hover:bg-[#D4CDB8]" data-testid="appt-email-send">
                      Dërgo email
                    </button>
                  </div>
                )}
              </div>
            ) : <div className="mt-1 text-sm text-[#5C5C5C]">Po ngarkohet…</div>}
          </section>

          {/* Time + status / EDIT MODE */}
          <section className="rounded-lg border border-[#E2DFD8] p-4 space-y-3">
            {editing ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="edit-start-time" className="text-xs uppercase tracking-[0.15em] text-[#5C5C5C] font-normal">Data dhe Ora</Label>
                    <Input
                      id="edit-start-time"
                      type="datetime-local"
                      value={editForm.start_time}
                      onChange={(e) => setEditForm({ ...editForm, start_time: e.target.value })}
                      data-testid="edit-start-time"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-duration" className="text-xs uppercase tracking-[0.15em] text-[#5C5C5C] font-normal">Kohëzgjatja</Label>
                    <Select
                      value={String(editForm.duration_min)}
                      onValueChange={(v) => setEditForm({ ...editForm, duration_min: parseInt(v, 10) })}
                    >
                      <SelectTrigger id="edit-duration" data-testid="edit-duration" className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[15, 30, 45, 60, 90, 120, 150, 180, 210, 240].map((m) => (
                          <SelectItem key={m} value={String(m)}>{m} min</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {doctors.length > 0 && (
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-[#5C5C5C] font-normal">Mjeku</Label>
                    <Select
                      value={editForm.doctor_id || "none"}
                      onValueChange={(v) => setEditForm({ ...editForm, doctor_id: v })}
                    >
                      <SelectTrigger data-testid="edit-doctor" className="mt-1">
                        <SelectValue placeholder="Zgjidh mjekun" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Pa mjek —</SelectItem>
                        {doctors.map((d) => (
                          <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-[#5C5C5C] font-normal">Arsyeja / Shërbimi</Label>
                  <Input
                    value={editForm.reason}
                    onChange={(e) => setEditForm({ ...editForm, reason: e.target.value })}
                    data-testid="edit-reason"
                    className="mt-1"
                    placeholder="p.sh. Kontroll periodik, Trajtim kanali..."
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-[#5C5C5C] font-normal">Shënime</Label>
                  <Textarea
                    value={editForm.notes}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                    data-testid="edit-notes"
                    className="mt-1"
                    rows={3}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="label-eyebrow flex items-center gap-1"><Clock size={12} /> Fillimi</div>
                    <div className="mt-1 font-medium">{start.toLocaleString("sq-AL", { dateStyle: "full", timeStyle: "short" })}</div>
                  </div>
                  <div>
                    <div className="label-eyebrow">Kohëzgjatja</div>
                    <div className="mt-1 font-medium">{appointment.duration_min || Math.round((end - start) / 60000)} min</div>
                  </div>
                </div>
                <div>
                  <div className="label-eyebrow">Arsyeja / Shërbimi</div>
                  <div className="mt-1 font-medium">{appointment.reason || "—"}</div>
                </div>
                <div>
                  <div className="label-eyebrow">Statusi</div>
                  <div className="mt-1 flex gap-2 flex-wrap">
                    {Object.entries(STATUS_LABEL).map(([k, lbl]) => (
                      <button key={k} type="button" disabled={busy} onClick={() => updateStatus(k)}
                              data-testid={`appt-status-${k}`}
                              className={`rounded-full px-3 py-1 text-xs font-medium border ${
                                appointment.status === k
                                  ? STATUS_COLOR[k]
                                  : "bg-white text-[#5C5C5C] border-[#E2DFD8] hover:border-[#2C4C3B]"
                              }`}>
                        {lbl}
                      </button>
                    ))}
                  </div>
                </div>
                {appointment.notes && (
                  <div>
                    <div className="label-eyebrow flex items-center gap-1"><FileText size={12} /> Shënime</div>
                    <div className="mt-1 text-sm whitespace-pre-wrap">{appointment.notes}</div>
                  </div>
                )}
              </>
            )}
          </section>

          {/* Chat log */}
          {appointment.chat_log && !editing && (
            <section className="rounded-lg border border-[#2C4C3B]/30 bg-[#2C4C3B]/5 p-4" data-testid="appt-chat-log">
              <div className="label-eyebrow flex items-center gap-1 text-[#2C4C3B]">
                <MessageSquare size={12} /> Kërkesa e Klientit (AI Chat)
              </div>
              <pre className="mt-2 max-h-72 overflow-y-auto text-xs font-mono whitespace-pre-wrap bg-white rounded-md p-3 border border-[#E2DFD8] text-[#1A1A1A]">
                {appointment.chat_log}
              </pre>
              <div className="mt-2 text-[10px] text-[#5C5C5C]">
                Ky takim u rezervua automatikisht përmes asistentit AI. Bisedën origjinale e shihni më sipër.
              </div>
            </section>
          )}

          <DialogFooter className="gap-2">
            {editing ? (
              <>
                <button className="btn-ghost" onClick={cancelEdits} disabled={busy} data-testid="appt-edit-cancel">
                  Anulo
                </button>
                <button className="btn-primary" onClick={saveEdits} disabled={busy} data-testid="appt-edit-save">
                  {busy ? "Duke ruajtur…" : "Ruaj Ndryshimet"}
                </button>
              </>
            ) : (
              <button className="btn-ghost" onClick={onClose} data-testid="appt-details-close">Mbyll</button>
            )}
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
